<template>
  <teleport to="head">
    <meta name="title" content="Portfolio | Journey As A Software Engineer" />
    <meta
      name="description"
      content="I'm an engineer who loves solving real problems end to end. That curiosity has taken me through a CS degree, startups, Google, and even founding my own company. Now I'm looking for my next team to build with."
    />
    <link rel="canonical" href="https://brandonaguirre.com/portfolio" />
  </teleport>
  <div id="hire_me_section">
    <div id="resume">
      <a
        href="https://brandonaguirre-website-images.s3.us-east-2.amazonaws.com/BrandonAguirreResume2021.pdf"
        target="_blank"
        download
        >Download my resume</a
      >
    </div>
    <div id="table_of_contents_wrap">
      <div id="head_section">
        <div id="photo_name_wrap">
          <img :src="getImageUrl('headshot.jpeg')" />
          <div>
            <h1>Brandon Aguirre</h1>
            <p>B.S. Computer Science</p>
          </div>
        </div>
        <div id="blurb_wrap">
          <p>
            I'm an engineer who loves solving real problems end to end. That
            curiosity has taken me through a CS degree, startups, Google, and
            even founding my own company. Now I'm looking for my next team to
            build with.
          </p>
        </div>
      </div>
      <hr />
      <div id="list_section">
        <hire-me-menu :contents="toc" />
      </div>
    </div>
    <div id="main_content_wrap">
      <suspense>
        <template #default>
          <hire-me-main :contents="toc" />
        </template>
        <template #fallback>
          <h1 style="text-align: center; margin-top: 30vh">
            <fa-icon class="spinner" :icon="['fa', 'spinner']" />
          </h1>
        </template>
      </suspense>
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from "vue";
import HireMeMenu from "./HireMe/HireMeMenu";

// async components
const HireMeMain = defineAsyncComponent(() => import("./HireMe/HireMeMain"));

export default {
  name: "HireMe",
  components: {
    HireMeMain,
    HireMeMenu,
  },
  data() {
    return {
      toc: [
        {
          text: "Pinstable - Founder & Engineer",
          date: "March 2026 - Current",
          logo: "pinstable",
          entry: {
            role: "Founder & Engineer",
            company: "Pinstable",
            location: "Longmont, CO",
            dates: "March 2026 – Present",
            bullets: [
              'Founded <strong>Pinstable</strong>, a cloud CMMS (maintenance software) built just for the pinball industry, and took it from an idea to a live product as the sole founder and engineer',
              'Built the entire stack myself: an <strong>offline-first installable PWA</strong>, a Kanban issue tracker, a <strong>maintenance-automation engine</strong> (time, usage, event, and webhook triggers), role-based access control, a <strong>Stripe-integrated payment system</strong>, a fleet analytics dashboard, and web and email notifications',
              'Set it up so players scan a <strong>QR code</strong> on any machine to report an issue, which the system turns into a prioritized work order and flags low inventory, connecting operators, players, and parts distributors in one place',
            ],
            narrative:
              "I started Pinstable on nights and weekends while I was at Google, then decided to leave and build it for real. Doing everything myself, from the code to the business side, has been the hardest and most rewarding thing I've worked on.",
            tech: ["vue", "django", "sass", "typescript", "python", "redis", "postgres", "heroku", "cloudflare"],
            links: [{ label: "pinstable.io", href: "https://www.pinstable.io" }],
          },
        },
        {
          text: "Google - Software Engineer",
          date: "June 2022 - March 2026",
          logo: "google",
          entry: {
            role: "Software Engineer",
            company: "Google",
            location: "Boulder, CO",
            dates: "June 2022 – March 2026",
            bullets: [
              'Improved navigation load times for <strong>hundreds of millions of Drive users</strong>. It was a user-facing change, so it took a lot of coordination with leadership to get it right',
              'Was a <strong>founding member of an experimentation-platform team</strong> that made it safer and more reliable for Drive engineers to run and validate their experiments',
              'Mentored several engineers as they joined the team, helping them get up to speed and start shipping',
            ],
            narrative:
              "I applied to a company I'd wanted to work at since I first started coding, and got in after a five-round interview loop (four of them technical). On Drive I went from performance work to helping start an experimentation-platform team.",
            tech: ["typescript", "java", "go", "+ internal Google tech"],
            links: [],
          },
        },
        {
          text: "Independent Projects & Freelance",
          date: "2020 - 2022",
          logo: "code",
          entry: {
            role: "Independent Projects & Freelance",
            company: "Independent",
            location: "Boulder, CO",
            dates: "2020 – 2022",
            bullets: [
              "Took some intentional time between roles to work on my own projects and recharge",
              'Built a <strong>production landscaping website for PDL</strong> as a freelance client',
              'Built projects to keep my skills sharp, including <strong>D3 algorithm visualizers</strong> (sorting and path-finding) and a <strong>2D physics engine</strong> written from scratch in TypeScript',
              'Worked through <strong>Crafting Interpreters</strong> and built my own interpreter (<strong>jlox</strong>) with my own notes, and pushed further into systems design and algorithmic problem-solving',
            ],
            narrative:
              "I spent this time working independently and building. Between a real client project and a few things I'd been meaning to make, it kept me sharp and let me go deeper than a day job usually allows.",
            tech: ["vue", "typescript", "js", "D3.js", "WordPress"],
            links: [
              { label: "PDL (live site)", href: "https://pdl.earth/" },
              { label: "Sorting visualizer", href: "https://brancisco.github.io/sorting-visualizer/" },
              { label: "Path-finding visualizer", href: "https://brancisco.github.io/visual-path-finding-algos/" },
              { label: "Physics engine", href: "/writing/js-physics-engine" },
              { label: "jlox (interpreter)", href: "https://github.com/brancisco/jlox" },
            ],
          },
        },
        {
          text: "Software Engineer at Boulder Startup",
          date: "May 2019 - Aug 2020 • 14 mos",
          initials: "FI",
          entry: {
            role: "Software Engineer",
            company: "Funeral Innovations",
            location: "Boulder, CO",
            dates: "May 2019 – Aug 2020 · 14 mos",
            bullets: [
              'Helped migrate the product off a legacy app and onto a modern stack, rebuilding the frontend in a new language and framework while porting over the core backend logic. It served <strong>hundreds of funeral homes</strong> and <strong>tens of thousands of end users</strong>',
              'Integrated <strong>Facebook, Google Ads, Stripe, and Twilio</strong> to automate ad campaigns, build custom reporting dashboards, process payments, and run SMS marketing',
            ],
            narrative:
              "My first full-time job out of CU. I wore a lot of hats here, from system design and full-stack work to dev-ops and testing.",
            photos: [
              {
                src: "FuneralInnovations.jpeg",
                text: "Team bonding at an escape room in Boulder.",
                location: "Pearl St. Boulder",
              },
            ],
            tech: [
              "php", "cakephp", "php_unit", "js", "html5", "css3", "sass", "vue",
              "jest", "aws", "docker", "git_hub", "bit_bucket", "git", "npm",
              "webpack", "google_ads", "stripe", "twilio",
            ],
            links: [],
          },
        },
        {
          text: "New Venture Challenge Finalist (Awarded $11,000)",
          date: "Mar 2019",
          initials: "CU",
          entry: {
            role: "New Venture Challenge Finalist",
            company: "CU Boulder",
            location: "Boulder, CO",
            dates: "Mar 2019",
            bullets: [
              'Built <strong>Nimb.ly</strong>, a scheduling &amp; staffing platform for the catering industry, as my senior capstone project',
              'Entered CU\'s <strong>New Venture Challenge</strong> and finished <strong>5th of 130+ teams</strong>, winning <strong>$11,000</strong>',
              'Pitched on stage at the Boulder Theater to an audience of <strong>1,000+</strong>, after 6 months of customer interviews, demos, and pitch prep',
            ],
            narrative: null,
            photos: [
              {
                src: "NVC_Brandon_Aguirre.jpeg",
                text: "Right before speaking on stage at Boulder Theater.",
                location: "Boulder Theater",
                locationHref: "https://goo.gl/maps/6RqrF5MxmL4cLZeL7",
              },
              {
                src: "Boulder_Theater_NVC2019.jpg",
                text: "Boulder Theater holding 1000+ audience members at the NVC Finals 2019.",
                location: "Boulder Theater",
                locationHref: "https://goo.gl/maps/6RqrF5MxmL4cLZeL7",
              },
            ],
            tech: ["python", "node", "express", "vue", "typescript", "js", "html5", "css3", "sass", "git_hub", "git"],
            links: [],
          },
        },
        {
          text: "Machine Learning Internship",
          date: "May 2018 - Sep 2018 • 5 mos",
          initials: "FI",
          entry: {
            role: "Machine Learning Intern",
            company: "Funeral Innovations",
            location: "Louisville, CO",
            dates: "May 2018 – Sep 2018 · 5 mos",
            bullets: [
              'Led an effort to build &amp; deploy a <strong>production-scale ML pipeline</strong> from the ground up, predicting lead-to-customer conversion likelihood from users\' online behavior',
              'We ran into a real-world ML problem: there wasn\'t enough training data to train a reliable model, so the team shipped a simpler <strong>heuristic-based recommendation</strong> built on the same signals',
            ],
            narrative:
              "It taught me that good ML starts with enough good data, and that a simple heuristic is sometimes the right call when you don't have it.",
            tech: ["aws", "sagemaker", "docker", "python", "scikitlearn", "numpy", "pandas", "matplotlib"],
            links: [],
          },
        },
        {
          text: "Software Engineer Internship",
          date: "May 2017 - Aug 2017 • 4 mos",
          initials: "FI",
          entry: {
            role: "Software Engineer Intern",
            company: "Funeral Innovations",
            location: "Boulder, CO",
            dates: "May 2017 – Aug 2017 · 4 mos",
            bullets: [
              'Built and deployed an in-house <strong>quiz platform</strong> that <strong>saved the company thousands of dollars</strong>, reached <strong>thousands of users</strong>, and was still in use when I left',
              'Initiated and built a <strong>configurable web crawler</strong> that eliminated repeated code across configurations',
            ],
            narrative: null,
            tech: ["mysql", "php", "cakephp", "jquery", "js", "html5", "css3"],
            links: [],
          },
        },
        {
          text: "Funded Undergraduate Research",
          date: "Sep 2016 - May 2017 • 9 mos",
          initials: "CU",
          entry: {
            role: "Undergraduate Research Assistant · BSI Scholar",
            company: "CU Boulder · Integrative Physiology of Aging Lab",
            location: "Boulder, CO",
            dates: "Sep 2016 – May 2017 · 9 mos",
            bullets: [
              'Developed reusable, well-organized code for a <strong>longitudinal study</strong> on the plasma metabolome and its correlation with physiological decline (Biological Age)',
              'Contributed to <strong>published</strong> research',
            ],
            narrative: null,
            tech: ["R", "python", "git", "git_hub"],
            links: [
              {
                label: "Read the publication",
                href: "https://scholar.colorado.edu/concern/graduate_thesis_or_dissertations/7p88cg57g",
              },
            ],
          },
        },
        {
          text: "Comp Sci Course Assistant & Tutor at CU Boulder",
          date: "Aug 2016 – Dec 2017 • 15 mos",
          initials: "CU",
          entry: {
            role: "CS Course Assistant & Tutor",
            company: "CU Boulder",
            location: "Boulder, CO",
            dates: "Aug 2016 – Dec 2017 · 15 mos",
            bullets: [
              "Ran group office hours to help students get through introductory computer science",
              "My first paid job in computer science, and I was thrilled to have it",
            ],
            narrative: null,
            tech: ["c++", "python"],
            links: [],
          },
        },
      ],
    };
  },
  mounted() {
    window.scrollTo(0, 0);
  },
  unmounted() {
    window.scrollTo(0, 0);
  },
};
</script>

<style scoped lang="scss">
@import "~@/assets/variables";

$menu-width: 300px;
$menu-padding: 10px;

#resume {
  position: fixed;
  z-index: 99;
  top: 65px;
  right: 10px;
  background: white;
  border-radius: 50px;
  box-shadow: 0 0 3px 1px rgba(0, 0, 0, 0.1);
  padding: 10px 15px;
  display: inline-block;
  a {
    color: $green;
  }
}

#hire_me_section {
  position: relative;
  height: 100%;
  #table_of_contents_wrap {
    padding: 0 ($menu-padding);
    width: $menu-width - (2 * $menu-padding);
    position: fixed;
    top: 50px;
    left: 0;
    height: calc(100% - 100px);
    max-height: calc(100% - 100px);
    overflow-y: scroll;

    #head_section {
      padding-top: 20px;
      #photo_name_wrap {
        display: flex;
        justify-content: space-around;
        align-content: center;

        > img {
          background: grey;
          border-radius: 50%;
          width: 100px;
          height: 100px;
        }

        > div {
          h1 {
            font-size: 1.3rem;
          }
        }
      }

      #blurb_wrap {
        text-align: center;
        line-height: 1.5rem;
      }
    }

    #list_section {
    }
  }

  #main_content_wrap {
    padding: 0px 20px 50px 10px;
    margin-left: $menu-width;

    h1 {
      margin: 0;
    }
  }
}

.spinner {
  color: $green;
  font-size: 3rem;
  animation: spin 1.2s infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media only screen and (max-width: 768px) {
  #hire_me_section {
    #table_of_contents_wrap {
      padding: 0 ($menu-padding);
      width: auto;
      position: relative;
      top: 0;
      left: 0;
      height: none;
      max-height: none;
      overflow: initial;

      .har {
        display: none;
      }

      #list_section {
        display: none;
      }
    }

    #main_content_wrap {
      padding: 0px 20px 50px 20px;
      margin: 0;
    }
  }

  #resume {
    top: initial;
    bottom: 15px;
  }
}
</style>
